import axios, {AxiosResponse} from 'axios'
import { h, Component } from 'preact'
import { t } from '../../libs/translationsUtils/translations'

import Editor from './index.js'
import redirect from '../../utils/redirect'

type PostRequestResponse = AxiosResponse<{
  errors: string[],
  success: string | undefined
}>
type PostRequest = (content: string, attachments: {}[], hasFileInProgress: boolean) => Promise<PostRequestResponse>

function newPost (): PostRequest {
  return (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return axios.post(window.location.href, {
      pagetext: content,
      attachments
    })
  }
}

function editPost (topicId: number, postId: number): PostRequest {
  return (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return axios.post('/post/edit', {
      'NewPostForm': {
        pagetext: content
      },
      attachments: attachments
    }, {
      params: {
        'postid': postId,
        'tid': topicId
      }
    })
  }
}

function newPostDestination (topicId:number): PostRequest {
  return (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return axios.post('/post/save', {
      post: { content },
      attachments
    }, {
      params: {
        topic_id: topicId
      }
    })
  }
}

function editPostDestination (topicId: number, postId: number): PostRequest {
  return (content, attachments, hasFileInProgress) => {
    checkAttachments(hasFileInProgress)
    return axios.post('/post/update', {
      post: { content },
      attachments
    }, {
      params: {
        post_id: postId,
        tid: topicId
      }
    })
  }
}

function determineRequest ({ postId, isControl, topicId }: {postId: number, isControl: boolean, topicId: number}) {
  if (!isControl) {
    if (postId) return editPostDestination(topicId, postId)
    return newPostDestination(topicId)
  }
  if (postId) return editPost(topicId, postId)
  return newPost()
}

function checkAttachments (hasFileInProgress: boolean) {
  if (hasFileInProgress) {
    const error = {
      response: {
        data: {
          errors: {
            pagetext: t('Common', 'file.upload.in.progress')
          }
        }
      }
    }
    throw error
  }
}

interface Props {
  postId: number
  topicId: number
  isControl: boolean
  redirect: boolean
  autoFocus?: boolean
  onSuccess: (content: string, response: PostRequestResponse) => void
  disableFileAttachments: boolean
}

interface State {
  attachments: {}[]
  errors: {errorMessage: string}[]
  isLoading: boolean
}

export default class extends Component<Props, State> {
  constructor () {
    super()
    this.state = {
      attachments: [],
      errors: [],
      isLoading: false
    }

  }

  async componentDidMount () {
    const postId = this.props.postId
    if (!postId) return

    const { data: attachments } = await axios.get(`/attachment/getForPost?postIds=${postId}`)
    if (!attachments || !attachments[postId]) return

    this.setState({ attachments: attachments[postId] })
  }

  render (props: Props) {
    const submitRequest = determineRequest(props)
    return <div>
      <Editor
        {...props}
        disableFileAttachments={props.disableFileAttachments}
        isControl={props.isControl}
        autoFocus={!!props.autoFocus}
        errors={this.state.errors}
        isLoading={this.state.isLoading}
        attachments={this.state.attachments}
        onSubmit={async (content: string, attachments: {}[], hasFileInProgress: boolean) => {
          this.setState({
            isLoading: true
          })
          try {
            const response = await submitRequest(content, attachments.map(item => JSON.stringify(item)), hasFileInProgress)
            const errors = response.data.errors
            if (errors && errors.length) {
              const newErrors = errors.map(error => {
                return { errorMessage: error }
              })
              this.setState({
                errors: this.state.errors.concat(newErrors),
                isLoading: false
              })
              return
            }
            if (props.redirect) {
              return redirect(response.data.success || response.headers.location)
            }
            props.onSuccess(content, response)
            this.setState({ errors: [] })
            window.CKEDITOR.instances['editor1'].setData('')
          } catch (error) {
            let errorMessage = error.response.data.errors.pagetext || error.response.data.errors || error.response.data.errors.content
            this.setState({
              errors: this.state.errors.concat({
                errorMessage
              })
            })
          }
          this.setState({
            isLoading: false
          })
        }
        } />
    </div>
  }
}
